/* eslint-disable no-underscore-dangle */
import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { DndContext, DragEndEvent, Modifier, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Api } from 'Utils/api';
import { areEqual } from 'Utils/equalityChecks';
import styles from 'Components/PdfFormTemplates/PdfFormEditor/pdfFormEditor.module.css';
import { PdfViewer } from './PdfViewer';
import { FieldOverlay } from './FieldOverlay';
import { FieldToolbox } from './FieldToolbox';
import { FieldProperties } from './FieldProperties';
import { pdfToScreen, screenToPdf } from './coordinateUtils';
import { TestFormModal } from './TestFormModal';
import { EditorField, TokenGroup, FALLBACK_TOKEN_GROUPS, setTokenData } from '../types';
import {
  pdfFormTemplateSelector,
  pdfFormFieldsSelector,
  fetchingPdfFormTemplatesSelector,
  pdfFormFieldsSavedSelector,
  pdfFormTemplateDeletedSelector,
} from '../selectors';
import { getPdfFormTemplate, savePdfFormFields, setFieldsSaved, setTemplateDeleted, fetchPdfFormTokens } from '../actions';
import { DeleteTemplateModal } from '../DeleteTemplateModal';

let clientIdCounter = 0;
function generateClientId() {
  clientIdCounter += 1;
  return `field_${Date.now()}_${clientIdCounter}`;
}

const DEFAULT_FIELD_SIZES: Record<string, { width: number; height: number }> = {
  text: { width: 150, height: 20 },
  checkbox: { width: 20, height: 20 },
  date: { width: 120, height: 20 },
  signature: { width: 200, height: 60 },
  initial: { width: 72, height: 28 },
  email: { width: 200, height: 20 },
  phone: { width: 150, height: 20 },
  number: { width: 140, height: 20 },
};

const DEFAULT_PAGE_HEIGHT = 792; // US Letter

function PdfFormEditor() {
  const AUTOSAVE_DELAY = 1200;
  const dispatch = useDispatch();
  const history = useHistory();
  const { templateId } = useParams<{ templateId: string }>();

  const template = useSelector(pdfFormTemplateSelector, areEqual);
  const serverFields = useSelector(pdfFormFieldsSelector, areEqual);
  const fetching = useSelector(fetchingPdfFormTemplatesSelector, areEqual);
  const fieldsSaved = useSelector(pdfFormFieldsSavedSelector, areEqual);
  const templateDeleted = useSelector(pdfFormTemplateDeletedSelector, areEqual);

  const [editorFields, setEditorFields] = useState<EditorField[]>([]);
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(new Set());
  const [templateName, setTemplateName] = useState('');
  const [numPages, setNumPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [snappingEnabled, setSnappingEnabled] = useState(true);
  const [toolboxCollapsed, setToolboxCollapsed] = useState(false);
  const [propertiesCollapsed, setPropertiesCollapsed] = useState(false);
  const [tokenGroups, setTokenGroups] = useState<TokenGroup[]>(FALLBACK_TOKEN_GROUPS);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [marquee, setMarquee] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [activeDrag, setActiveDrag] = useState<{ clientId: string; transform: { x: number; y: number } } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const titleAutosaveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const autosaveInitializedRef = useRef(false);
  const lastSavedSnapshotRef = useRef('');
  const lastSavedTemplateNameRef = useRef('');
  const inFlightFieldSaveRef = useRef<{ snapshot: string; promise: Promise<boolean> } | null>(null);
  const inFlightTitleSaveRef = useRef<{ name: string; promise: Promise<boolean> } | null>(null);
  const isMountedRef = useRef(true);
  const marqueeRef = useRef<{
    startX: number;
    startY: number;
    additive: boolean;
    baseSelection: Set<string>;
    active: boolean;
    pointerId: number;
  } | null>(null);
  // Track height per page number for correct coordinate conversion
  const pageHeightsRef = useRef<Record<number, number>>({});
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const pdfCanvasWrapperRef = useRef<HTMLDivElement>(null);
  const fieldsInitialized = useRef(false);
  const pdfDocRef = useRef<any>(null);
  const initialScaleSet = useRef(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 3 } }));

  useEffect(() => {
    if (templateId) {
      // Reset editor state when navigating to a different template
      setEditorFields([]);
      setSelectedFieldIds(new Set());
      setCurrentPage(1);
      setNumPages(1);
      pageHeightsRef.current = {};
      fieldsInitialized.current = false;
      initialScaleSet.current = false;
      pdfDocRef.current = null;
      autosaveInitializedRef.current = false;
      lastSavedSnapshotRef.current = '';
      lastSavedTemplateNameRef.current = '';
      clearTimeout(autosaveTimerRef.current);
      clearTimeout(titleAutosaveTimerRef.current);

      dispatch(getPdfFormTemplate(templateId) as any);
    }
  }, [templateId]);

  // Fetch available tokens from the API; keep fallback on failure
  useEffect(() => {
    dispatch(fetchPdfFormTokens() as any).then((apiGroups: any[]) => {
      if (apiGroups.length === 0) return;
      const groups: TokenGroup[] = apiGroups.map((g) => ({
        label: g.category,
        tokens: g.tokens.map((t: any) => ({ label: t.label, value: t.token, type: t.type })),
      }));
      setTokenGroups(groups);
      setTokenData(groups);
    });
  }, []);

  // Sync template name when template loads
  useEffect(() => {
    if (template?.name !== undefined) {
      setTemplateName(template.name);
      lastSavedTemplateNameRef.current = template.name;
      clearTimeout(titleAutosaveTimerRef.current);
    }
  }, [template?.name]);

  // When all page heights are gathered and server fields arrive, convert coordinates
  useEffect(() => {
    if (serverFields?.length > 0 && !fieldsInitialized.current && Object.keys(pageHeightsRef.current).length > 0) {
      fieldsInitialized.current = true;
      const converted: EditorField[] = serverFields.map((f: any) => {
        const ph = pageHeightsRef.current[f.page] || DEFAULT_PAGE_HEIGHT;
        const screen = pdfToScreen(f.x, f.y, f.width, f.height, ph, 1);
        return {
          ...f,
          _clientId: generateClientId(),
          x: screen.x,
          y: screen.y,
          width: screen.width,
          height: screen.height,
        };
      });
      autosaveInitializedRef.current = false;
      setEditorFields(converted);
    }
  }, [serverFields]);

  const [showSaved, setShowSaved] = useState(false);

  const savedTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!fieldsSaved) return;
    dispatch(setFieldsSaved(false) as any);
    setShowSaved(true);
    clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setShowSaved(false), 2000);
  }, [fieldsSaved]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      clearTimeout(savedTimerRef.current);
      clearTimeout(autosaveTimerRef.current);
      clearTimeout(titleAutosaveTimerRef.current);
    },
    []
  );

  // Navigate back to list after template deletion
  useEffect(() => {
    if (templateDeleted) {
      dispatch(setTemplateDeleted(false) as any);
      history.push('/pdf-forms');
    }
  }, [templateDeleted]);

  const onDeleteClose = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  const onDocumentLoadSuccess = useCallback(
    async (pdf: any) => {
      setNumPages(pdf.numPages);
      pdfDocRef.current = pdf;

      // Pre-fetch all page heights for correct coordinate conversion
      const heights: Record<number, number> = {};
      for (let i = 1; i <= pdf.numPages; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1 });
        heights[i] = viewport.height;
      }
      pageHeightsRef.current = heights;

      // If server fields are already loaded, trigger conversion now
      if (serverFields?.length > 0 && !fieldsInitialized.current) {
        fieldsInitialized.current = true;
        const converted: EditorField[] = serverFields.map((f: any) => {
          const ph = heights[f.page] || DEFAULT_PAGE_HEIGHT;
          const screen = pdfToScreen(f.x, f.y, f.width, f.height, ph, 1);
          return {
            ...f,
            _clientId: generateClientId(),
            x: screen.x,
            y: screen.y,
            width: screen.width,
            height: screen.height,
          };
        });
        autosaveInitializedRef.current = false;
        setEditorFields(converted);
      }
    },
    [serverFields]
  );

  const fitToWindow = useCallback(() => {
    const wrapper = pdfCanvasWrapperRef.current;
    const height = pageHeightsRef.current[currentPage];
    // Use first page height as fallback for width estimate
    const width = Object.values(pageHeightsRef.current)[0]
      ? (pdfDocRef.current ? undefined : undefined)
      : undefined;
    // We need the page width — grab it from the rendered canvas if available
    const canvas = wrapper?.querySelector('canvas');
    const pdfWidth = canvas ? canvas.width / (window.devicePixelRatio || 1) / (scale || 1) : undefined;

    if (!wrapper || !height) return;
    const pageW = pdfWidth || height * 0.773; // US Letter aspect ratio fallback
    const padding = 40;
    const availableHeight = wrapper.clientHeight - padding;
    const availableWidth = wrapper.clientWidth - padding;
    const scaleByHeight = availableHeight / height;
    const scaleByWidth = availableWidth / pageW;
    const fitScale = Math.min(scaleByHeight, scaleByWidth);
    const clamped = Math.max(0.5, Math.min(2, fitScale));
    setScale(Math.round(clamped * 20) / 20);
  }, [currentPage, scale]);

  const onPageLoadSuccess = useCallback(
    (page: any) => {
      const height = page.originalHeight;
      const width = page.originalWidth;
      pageHeightsRef.current[currentPage] = height;

      // Auto-fit scale on first page load
      if (!initialScaleSet.current && pdfCanvasWrapperRef.current && height && width) {
        initialScaleSet.current = true;
        const wrapper = pdfCanvasWrapperRef.current;
        const padding = 40;
        const availableHeight = wrapper.clientHeight - padding;
        const availableWidth = wrapper.clientWidth - padding;
        const scaleByHeight = availableHeight / height;
        const scaleByWidth = availableWidth / width;
        const fitScale = Math.min(scaleByHeight, scaleByWidth);
        const clamped = Math.max(0.5, Math.min(2, fitScale));
        setScale(Math.round(clamped * 20) / 20);
      }
    },
    [currentPage]
  );

  // Re-fit when panels collapse/expand
  useEffect(() => {
    if (!initialScaleSet.current) return;
    // Small delay to let the panel CSS transition finish
    const timer = setTimeout(fitToWindow, 250);
    return () => clearTimeout(timer);
  }, [toolboxCollapsed, propertiesCollapsed]);

  // Re-fit on window resize
  useEffect(() => {
    if (!initialScaleSet.current) return undefined;
    let timer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(fitToWindow, 200);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [fitToWindow]);

  const SNAP_THRESHOLD = 12; // pixels in unscaled coordinates
  const SNAP_PROXIMITY = 150; // only snap to fields within this distance

  const [snapGuides, setSnapGuides] = useState<{ x?: number; y?: number } | null>(null);
  const snapGuidesRef = useRef<{ x?: number; y?: number } | null>(null);

  const snapPosition = useCallback(
    (draggedField: EditorField, newX: number, newY: number) => {
      if (!snappingEnabled) {
        return {
          x: newX,
          y: newY,
          guideX: undefined,
          guideY: undefined,
        };
      }

      let snappedX = newX;
      let snappedY = newY;

      const dragCenterX = newX + draggedField.width / 2;
      const dragCenterY = newY + draggedField.height / 2;

      const excludeSelectedGroup = selectedFieldIds.has(draggedField._clientId) && selectedFieldIds.size > 1;
      const others = editorFields.filter((f) => {
        if (f._clientId === draggedField._clientId || f.page !== currentPage) return false;
        if (excludeSelectedGroup && selectedFieldIds.has(f._clientId)) return false;
        // Only consider fields within proximity
        const otherCenterX = f.x + f.width / 2;
        const otherCenterY = f.y + f.height / 2;
        const dist = Math.sqrt((dragCenterX - otherCenterX) ** 2 + (dragCenterY - otherCenterY) ** 2);
        return dist < SNAP_PROXIMITY;
      });

      let closestDx = SNAP_THRESHOLD + 1;
      let closestDy = SNAP_THRESHOLD + 1;
      let guideX: number | undefined;
      let guideY: number | undefined;

      const dragRight = newX + draggedField.width;
      const dragBottom = newY + draggedField.height;

      others.forEach((other) => {
        const otherRight = other.x + other.width;
        const otherBottom = other.y + other.height;

        // Snap X: left-to-left, left-to-right, right-to-left, right-to-right
        const xSnaps = [
          { dist: Math.abs(newX - other.x), val: other.x, guide: other.x },
          { dist: Math.abs(newX - otherRight), val: otherRight, guide: otherRight },
          { dist: Math.abs(dragRight - other.x), val: other.x - draggedField.width, guide: other.x },
          { dist: Math.abs(dragRight - otherRight), val: otherRight - draggedField.width, guide: otherRight },
        ];
        xSnaps.forEach(({ dist, val, guide }) => {
          if (dist < closestDx) { closestDx = dist; snappedX = val; guideX = guide; }
        });

        // Snap Y: top-to-top, top-to-bottom, bottom-to-top, bottom-to-bottom
        const ySnaps = [
          { dist: Math.abs(newY - other.y), val: other.y, guide: other.y },
          { dist: Math.abs(newY - otherBottom), val: otherBottom, guide: otherBottom },
          { dist: Math.abs(dragBottom - other.y), val: other.y - draggedField.height, guide: other.y },
          { dist: Math.abs(dragBottom - otherBottom), val: otherBottom - draggedField.height, guide: otherBottom },
        ];
        ySnaps.forEach(({ dist, val, guide }) => {
          if (dist < closestDy) { closestDy = dist; snappedY = val; guideY = guide; }
        });
      });

      const didSnapX = closestDx <= SNAP_THRESHOLD;
      const didSnapY = closestDy <= SNAP_THRESHOLD;

      return {
        x: didSnapX ? snappedX : newX,
        y: didSnapY ? snappedY : newY,
        guideX: didSnapX ? guideX : undefined,
        guideY: didSnapY ? guideY : undefined,
      };
    },
    [editorFields, currentPage, selectedFieldIds, snappingEnabled]
  );

  // Helper to update snap guides only when values actually change
  const updateSnapGuides = useCallback((newGuides: { x?: number; y?: number } | null) => {
    const prev = snapGuidesRef.current;
    const same = prev?.x === newGuides?.x && prev?.y === newGuides?.y
      && (prev === null) === (newGuides === null);
    if (!same) {
      snapGuidesRef.current = newGuides;
      setSnapGuides(newGuides);
    }
  }, []);

  const activeDragRef = useRef<{ clientId: string; transform: { x: number; y: number } } | null>(null);

  const clearActiveDrag = useCallback(() => {
    updateSnapGuides(null);
    activeDragRef.current = null;
    setActiveDrag(null);
  }, [updateSnapGuides]);

  useEffect(() => {
    if (!snappingEnabled) updateSnapGuides(null);
  }, [snappingEnabled, updateSnapGuides]);

  // Modifier that snaps the dragged field to nearby edges in real-time
  const snapModifier: Modifier = useCallback(
    ({ transform, active }) => {
      if (!active?.data?.current || active.data.current.type !== 'existing-field') {
        clearActiveDrag();
        return transform;
      }
      const field = active.data.current.field as EditorField;
      const previewTransform = {
        x: transform.x,
        y: transform.y,
      };

      if (snappingEnabled) {
        const rawX = field.x + transform.x / scale;
        const rawY = field.y + transform.y / scale;
        const snapped = snapPosition(field, rawX, rawY);

        if (snapped.guideX !== undefined || snapped.guideY !== undefined) {
          updateSnapGuides({ x: snapped.guideX, y: snapped.guideY });
        } else {
          updateSnapGuides(null);
        }

        previewTransform.x = (snapped.x - field.x) * scale;
        previewTransform.y = (snapped.y - field.y) * scale;
      } else {
        updateSnapGuides(null);
      }

      // Track for group-move of other selected fields
      const prev = activeDragRef.current;
      if (!prev || prev.clientId !== (active.id as string) || prev.transform.x !== previewTransform.x || prev.transform.y !== previewTransform.y) {
        const next = { clientId: active.id as string, transform: previewTransform };
        activeDragRef.current = next;
        setActiveDrag(next);
      }

      return { ...transform, ...previewTransform };
    },
    [clearActiveDrag, scale, snapPosition, snappingEnabled, updateSnapGuides]
  );

  const MIN_RESIZE_WIDTH = 20;
  const MIN_RESIZE_HEIGHT = 14;

  const snapDimensions = useCallback(
    (field: EditorField, newW: number, newH: number) => {
      if (!snappingEnabled) {
        return {
          width: newW,
          height: newH,
        };
      }

      let snappedW = newW;
      let snappedH = newH;
      const others = editorFields.filter(
        (f) => f._clientId !== field._clientId && f.page === currentPage
      );

      let closestDw = SNAP_THRESHOLD + 1;
      let closestDh = SNAP_THRESHOLD + 1;

      others.forEach((other) => {
        // Ignore fields below minimum resize dimensions as snap targets
        if (other.width >= MIN_RESIZE_WIDTH) {
          const dw = Math.abs(newW - other.width);
          if (dw < closestDw) { closestDw = dw; snappedW = other.width; }
        }

        if (other.height >= MIN_RESIZE_HEIGHT) {
          const dh = Math.abs(newH - other.height);
          if (dh < closestDh) { closestDh = dh; snappedH = other.height; }
        }
      });

      return {
        width: closestDw <= SNAP_THRESHOLD ? snappedW : newW,
        height: closestDh <= SNAP_THRESHOLD ? snappedH : newH,
      };
    },
    [editorFields, currentPage, snappingEnabled]
  );

  const undoStackRef = useRef<EditorField[][]>([]);
  const redoStackRef = useRef<EditorField[][]>([]);
  const MAX_UNDO = 50;

  const pushUndo = useCallback(() => {
    undoStackRef.current.push(editorFields.map((f) => ({ ...f })));
    if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift();
    redoStackRef.current = [];
  }, [editorFields]);

  const undo = useCallback(() => {
    const prev = undoStackRef.current.pop();
    if (!prev) return;
    redoStackRef.current.push(editorFields.map((f) => ({ ...f })));
    setEditorFields(prev);
  }, [editorFields]);

  const redo = useCallback(() => {
    const next = redoStackRef.current.pop();
    if (!next) return;
    undoStackRef.current.push(editorFields.map((f) => ({ ...f })));
    setEditorFields(next);
  }, [editorFields]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      clearActiveDrag();
      const { active, delta } = event;
      const data = active.data.current;

      if (data?.type === 'new-field') {
        const fieldType = data.fieldType as string;
        const defaults = DEFAULT_FIELD_SIZES[fieldType] || DEFAULT_FIELD_SIZES.text;

        const container = pdfContainerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();

        const activeRect = active.rect.current?.translated;
        if (!activeRect) return;

        const x = (activeRect.left - rect.left) / scale;
        const y = (activeRect.top - rect.top) / scale;

        if (x >= 0 && y >= 0) {
          pushUndo();
          const newField: EditorField = {
            _clientId: generateClientId(),
            field_name: fieldType === 'date' ? '{{current_date}}' : '',
            field_type: fieldType as EditorField['field_type'],
            numeric_format: fieldType === 'number' ? 'number' : undefined,
            page: currentPage,
            x,
            y,
            width: defaults.width,
            height: defaults.height,
            font_size: fieldType !== 'checkbox' && fieldType !== 'signature' && fieldType !== 'initial' ? 12 : undefined,
            user_fillable: fieldType !== 'signature' && fieldType !== 'initial',
          };
          setEditorFields((prev) => [...prev, newField]);
          setSelectedFieldIds(new Set([newField._clientId]));
        }
      } else if (data?.type === 'existing-field') {
        pushUndo();
        const clientId = active.id as string;
        const draggedField = data.field as EditorField;
        // Compute snapped position for the dragged field
        const rawX = draggedField.x + delta.x / scale;
        const rawY = draggedField.y + delta.y / scale;
        const snapped = snapPosition(draggedField, rawX, rawY);
        const snapDx = snapped.x - draggedField.x;
        const snapDy = snapped.y - draggedField.y;

        // If the dragged field is part of a multi-selection, move all selected fields
        const movingIds = selectedFieldIds.has(clientId) && selectedFieldIds.size > 1
          ? new Set(editorFields.filter((f) => f.page === currentPage && selectedFieldIds.has(f._clientId)).map((f) => f._clientId))
          : new Set([clientId]);

        setEditorFields((prev) =>
          prev.map((f) => {
            if (!movingIds.has(f._clientId)) return f;
            return { ...f, x: f.x + snapDx, y: f.y + snapDy };
          })
        );
      }
    },
    [clearActiveDrag, currentPage, editorFields, scale, snapPosition, pushUndo, selectedFieldIds]
  );

  const onDragCancel = useCallback(() => {
    clearActiveDrag();
  }, [clearActiveDrag]);

  const onResizeBegin = useCallback(() => {
    pushUndo();
    setIsResizing(true);
  }, [pushUndo]);

  const onResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  const onFieldSelect = useCallback((clientId: string) => {
    setSelectedFieldIds(new Set([clientId]));
  }, []);

  const onFieldToggleSelect = useCallback((clientId: string) => {
    setSelectedFieldIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);

  const onFieldResize = useCallback((clientId: string, width: number, height: number, x?: number, y?: number) => {
    setEditorFields((prev) =>
      prev.map((f) => {
        if (f._clientId !== clientId) return f;
        const updates: Partial<EditorField> = { width, height };
        if (x !== undefined) updates.x = x;
        if (y !== undefined) updates.y = y;
        return { ...f, ...updates };
      })
    );
  }, []);

  const onFieldUpdate = useCallback(
    (clientId: string, updates: Partial<EditorField>) => {
      const hasChanges = editorFields.some(
        (f) =>
          f._clientId === clientId &&
          Object.entries(updates).some(([key, value]) => f[key as keyof EditorField] !== value)
      );
      if (!hasChanges) return;

      pushUndo();
      setEditorFields((prev) =>
        prev.map((f) => {
          if (f._clientId === clientId) return { ...f, ...updates };
          return f;
        })
      );
    },
    [editorFields, pushUndo]
  );

  const onBulkUpdate = useCallback(
    (updates: Partial<EditorField>, predicate?: (field: EditorField) => boolean) => {
      const hasChanges = editorFields.some(
        (f) =>
          selectedFieldIds.has(f._clientId) &&
          (!predicate || predicate(f)) &&
          Object.entries(updates).some(([key, value]) => f[key as keyof EditorField] !== value)
      );
      if (!hasChanges) return;

      pushUndo();
      setEditorFields((prev) =>
        prev.map((f) => (selectedFieldIds.has(f._clientId) && (!predicate || predicate(f)) ? { ...f, ...updates } : f))
      );
    },
    [editorFields, selectedFieldIds, pushUndo]
  );

  const onTemplateNameSave = useCallback(
    async (nameSource = templateName, options?: { revertInvalid?: boolean }) => {
      if (!template?.id) return false;
      clearTimeout(titleAutosaveTimerRef.current);
      const normalizedName = nameSource.trim();
      if (!normalizedName) {
        if (options?.revertInvalid && isMountedRef.current) {
          setTemplateName(lastSavedTemplateNameRef.current || template.name);
        }
        return false;
      }
      if (normalizedName === lastSavedTemplateNameRef.current) return true;

      if (inFlightTitleSaveRef.current) {
        if (inFlightTitleSaveRef.current.name === normalizedName) return inFlightTitleSaveRef.current.promise;
        await inFlightTitleSaveRef.current.promise;
        if (normalizedName === lastSavedTemplateNameRef.current) return true;
      }

      const promise = (async () => {
        try {
          await Api.put(`/pdf-form-templates/${template.id}`, { name: normalizedName });
          lastSavedTemplateNameRef.current = normalizedName;
          return true;
        } catch {
          // Revert on failure
          if (isMountedRef.current) {
            setTemplateName(lastSavedTemplateNameRef.current || template.name);
          }
          return false;
        } finally {
          if (inFlightTitleSaveRef.current?.name === normalizedName) {
            inFlightTitleSaveRef.current = null;
          }
        }
      })();

      inFlightTitleSaveRef.current = { name: normalizedName, promise };
      return promise;
    },
    [template?.id, template?.name, templateName]
  );

  const onFieldDelete = useCallback(
    (clientId: string) => {
      pushUndo();
      setEditorFields((prev) => prev.filter((f) => f._clientId !== clientId));
      setSelectedFieldIds((prev) => {
        if (!prev.has(clientId)) return prev;
        const next = new Set(prev);
        next.delete(clientId);
        return next;
      });
    },
    [pushUndo]
  );

  const onBulkDelete = useCallback(() => {
    pushUndo();
    setSelectedFieldIds((ids) => {
      setEditorFields((prev) => prev.filter((f) => !ids.has(f._clientId)));
      return new Set();
    });
  }, [pushUndo]);

  const clipboardFieldsRef = useRef<EditorField[]>([]);

  // Keyboard shortcuts: Delete, Copy (Ctrl/Cmd+C), Paste (Ctrl/Cmd+V), Select All (Ctrl/Cmd+A)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // Delete selected fields
      if (selectedFieldIds.size > 0 && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        onBulkDelete();
        return;
      }

      const mod = e.metaKey || e.ctrlKey;

      // Undo
      if (mod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }

      // Redo (Ctrl+Shift+Z or Ctrl+Y)
      if ((mod && e.key === 'Z') || (mod && e.key === 'z' && e.shiftKey) || (mod && e.key === 'y')) {
        e.preventDefault();
        redo();
        return;
      }

      // Select All on current page
      if (mod && e.key === 'a') {
        e.preventDefault();
        const pageFieldIds = editorFields
          .filter((f) => f.page === currentPage)
          .map((f) => f._clientId);
        setSelectedFieldIds(new Set(pageFieldIds));
        return;
      }

      // Copy selected fields
      if (mod && e.key === 'c' && selectedFieldIds.size > 0) {
        e.preventDefault();
        clipboardFieldsRef.current = editorFields.filter((f) => selectedFieldIds.has(f._clientId)).map((f) => ({ ...f }));
      }

      // Paste copied fields
      if (mod && e.key === 'v' && clipboardFieldsRef.current.length > 0) {
        e.preventDefault();
        pushUndo();
        const newIds = new Set<string>();
        const newFields = clipboardFieldsRef.current.map((src) => {
          const newField: EditorField = {
            ...src,
            _clientId: generateClientId(),
            id: undefined,
            x: src.x + 15,
            y: src.y + 15,
            page: currentPage,
          };
          newIds.add(newField._clientId);
          return newField;
        });
        setEditorFields((prev) => [...prev, ...newFields]);
        setSelectedFieldIds(newIds);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedFieldIds, onBulkDelete, editorFields, currentPage, undo, redo, pushUndo]);

  const buildSaveFields = useCallback((fieldsSource: EditorField[]) =>
    fieldsSource.map((f, idx) => {
      // Use the correct page height for each field's page
      const ph = pageHeightsRef.current[f.page] || DEFAULT_PAGE_HEIGHT;
      const pdfCoords = screenToPdf(f.x, f.y, f.width, f.height, ph, 1);
      return {
        id: f.id || undefined,
        field_name: f.field_name || `field_${idx + 1}`,
        field_type: f.field_type,
        numeric_format: f.field_type === 'number' ? (f.numeric_format || 'number') : undefined,
        page: f.page,
        x: Math.round(pdfCoords.x * 100) / 100,
        y: Math.round(pdfCoords.y * 100) / 100,
        width: Math.round(pdfCoords.width * 100) / 100,
        height: Math.round(pdfCoords.height * 100) / 100,
        font_size: f.font_size,
        user_fillable: f.field_type === 'signature' || f.field_type === 'initial' ? false : f.user_fillable !== false,
        // Signature/initial fields are mark fields that are always mandatory to
        // sign. The signing backend enforces every mark, and iOS only treats a
        // mark as required when its `required` flag is set — so persist these as
        // required to avoid a Submit dead-end on device.
        required: f.field_type === 'signature' || f.field_type === 'initial' ? true : !!f.required,
      };
    }),
  []);

  const onSave = useCallback(
    async (fieldsSource = editorFields) => {
      if (!template?.id) return false;
      clearTimeout(autosaveTimerRef.current);
      const fields = buildSaveFields(fieldsSource);
      const snapshot = JSON.stringify(fields);
      if (snapshot === lastSavedSnapshotRef.current) return true;

      if (inFlightFieldSaveRef.current) {
        if (inFlightFieldSaveRef.current.snapshot === snapshot) return inFlightFieldSaveRef.current.promise;
        await inFlightFieldSaveRef.current.promise;
        if (snapshot === lastSavedSnapshotRef.current) return true;
      }

      const promise = (async () => {
        try {
          const saved = await dispatch(savePdfFormFields(template.id, fields) as any);
          if (saved) {
            lastSavedSnapshotRef.current = snapshot;
          }
          return saved;
        } finally {
          if (inFlightFieldSaveRef.current?.snapshot === snapshot) {
            inFlightFieldSaveRef.current = null;
          }
        }
      })();

      inFlightFieldSaveRef.current = { snapshot, promise };
      return promise;
    },
    [buildSaveFields, dispatch, editorFields, template?.id]
  );

  useEffect(() => {
    if (!template?.id) return undefined;

    const snapshot = JSON.stringify(buildSaveFields(editorFields));
    if (!autosaveInitializedRef.current) {
      autosaveInitializedRef.current = true;
      lastSavedSnapshotRef.current = snapshot;
      return undefined;
    }

    if (snapshot === lastSavedSnapshotRef.current || activeDrag || isResizing) {
      clearTimeout(autosaveTimerRef.current);
      return undefined;
    }

    clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      onSave().catch(() => {});
    }, AUTOSAVE_DELAY);

    return () => clearTimeout(autosaveTimerRef.current);
  }, [AUTOSAVE_DELAY, activeDrag, buildSaveFields, editorFields, isResizing, onSave, template?.id]);

  useEffect(() => {
    if (!template?.id) return undefined;

    const normalizedName = templateName.trim();
    if (normalizedName === lastSavedTemplateNameRef.current) {
      clearTimeout(titleAutosaveTimerRef.current);
      return undefined;
    }

    if (!normalizedName) {
      clearTimeout(titleAutosaveTimerRef.current);
      return undefined;
    }

    clearTimeout(titleAutosaveTimerRef.current);
    titleAutosaveTimerRef.current = setTimeout(() => {
      onTemplateNameSave().catch(() => {});
    }, AUTOSAVE_DELAY);

    return () => clearTimeout(titleAutosaveTimerRef.current);
  }, [AUTOSAVE_DELAY, onTemplateNameSave, template?.id, templateName]);

  const flushPendingSaves = useCallback(async () => {
    let saved = true;
    const fieldSnapshot = JSON.stringify(buildSaveFields(editorFields));
    if (template?.id && fieldSnapshot !== lastSavedSnapshotRef.current) {
      saved = (await onSave(editorFields)) && saved;
    }

    if (template?.id && templateName !== lastSavedTemplateNameRef.current) {
      saved = (await onTemplateNameSave(templateName, { revertInvalid: true })) && saved;
    }
    return saved;
  }, [buildSaveFields, editorFields, onSave, onTemplateNameSave, template?.id, templateName]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!template?.id) return;

      const fieldSnapshot = JSON.stringify(buildSaveFields(editorFields));
      const normalizedName = templateName.trim();
      const hasPendingFieldChanges = fieldSnapshot !== lastSavedSnapshotRef.current;
      const hasPendingTitleChanges = templateName !== lastSavedTemplateNameRef.current
        && (normalizedName !== lastSavedTemplateNameRef.current || normalizedName === '');
      if (!hasPendingFieldChanges && !hasPendingTitleChanges) return;

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [buildSaveFields, editorFields, template?.id, templateName]);

  const onBack = useCallback(async () => {
    const saved = await flushPendingSaves();
    if (saved) history.push('/pdf-forms');
  }, [flushPendingSaves, history]);

  const onDeselect = useCallback((e: React.MouseEvent) => {
    // Don't deselect if a marquee drag just finished (marqueeRef is cleared in pointerup)
    // or if the click landed on a field
    if ((e.target as HTMLElement).closest('[data-field-overlay]')) return;
    setSelectedFieldIds(new Set());
  }, []);

  // Refs for latest values so window-level listeners don't go stale
  const editorFieldsRef = useRef(editorFields);
  editorFieldsRef.current = editorFields;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const scaleRef = useRef(scale);
  scaleRef.current = scale;

  const computeMarqueeSelection = useCallback(
    (m: { startX: number; startY: number; endX: number; endY: number }, base: Set<string>, additive: boolean) => {
      const mLeft = Math.min(m.startX, m.endX);
      const mRight = Math.max(m.startX, m.endX);
      const mTop = Math.min(m.startY, m.endY);
      const mBottom = Math.max(m.startY, m.endY);

      const intersecting = new Set<string>();
      editorFieldsRef.current.forEach((f) => {
        if (f.page !== currentPageRef.current) return;
        if (f.x + f.width > mLeft && f.x < mRight && f.y + f.height > mTop && f.y < mBottom) {
          intersecting.add(f._clientId);
        }
      });

      if (additive) {
        const merged = new Set(base);
        intersecting.forEach((id) => merged.add(id));
        return merged;
      }
      return intersecting;
    },
    []
  );

  const onMarqueePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only start marquee from empty space (not from fields)
      if ((e.target as HTMLElement).closest('[data-field-overlay]')) return;
      // Only primary button
      if (e.button !== 0) return;

      const container = pdfContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = (e.clientX - rect.left) / scaleRef.current;
      const y = (e.clientY - rect.top) / scaleRef.current;
      const additive = e.shiftKey || e.metaKey || e.ctrlKey;

      marqueeRef.current = {
        startX: x,
        startY: y,
        additive,
        baseSelection: additive ? new Set(selectedFieldIds) : new Set(),
        active: false,
        pointerId: e.pointerId,
      };

      // Capture pointer so move/up fire even outside the wrapper
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [selectedFieldIds]
  );

  const onMarqueePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const state = marqueeRef.current;
      if (!state) return;

      const container = pdfContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = (e.clientX - rect.left) / scaleRef.current;
      const y = (e.clientY - rect.top) / scaleRef.current;

      // Threshold in screen pixels (consistent regardless of zoom level)
      if (!state.active) {
        const dxScreen = (x - state.startX) * scaleRef.current;
        const dyScreen = (y - state.startY) * scaleRef.current;
        if (Math.abs(dxScreen) < 3 && Math.abs(dyScreen) < 3) return;
        state.active = true;
      }

      const m = { startX: state.startX, startY: state.startY, endX: x, endY: y };
      setMarquee(m);
      setSelectedFieldIds(computeMarqueeSelection(m, state.baseSelection, state.additive));
    },
    [computeMarqueeSelection]
  );

  const onMarqueePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const state = marqueeRef.current;
      if (!state) return;

      // Release pointer capture
      try { (e.currentTarget as HTMLElement).releasePointerCapture(state.pointerId); } catch { /* already released */ }

      const wasActive = state.active;
      marqueeRef.current = null;
      setMarquee(null);

      // If marquee was active, suppress the click event that follows pointerup
      // so onDeselect doesn't clear the selection we just made
      if (wasActive) {
        const wrapper = pdfCanvasWrapperRef.current;
        if (wrapper) {
          const suppressClick = (ev: Event) => { ev.stopPropagation(); };
          wrapper.addEventListener('click', suppressClick, { capture: true, once: true });
        }
      }
    },
    []
  );

  const selectedFields = editorFields.filter((f) => selectedFieldIds.has(f._clientId));

  if (fetching && !template) {
    return <div className={styles.loadingOverlay}>Loading template...</div>;
  }

  if (!template) {
    return <div className={styles.loadingOverlay}>Template not found</div>;
  }

  const fieldCount = editorFields.length;
  const fieldCountLabel = fieldCount === 1 ? 'field' : 'fields';

  return (
    <DndContext sensors={sensors} modifiers={[snapModifier]} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
      <div className={styles.editorLayout}>
        <div className={`${styles.toolboxPanel} ${toolboxCollapsed ? styles.toolboxPanelCollapsed : ''}`}>
          <div className={styles.panelHeader}>
            {!toolboxCollapsed && <h3>Field Types</h3>}
            <button
              type="button"
              className={styles.panelToggleBtn}
              onClick={() => setToolboxCollapsed((c) => !c)}
            >
              {toolboxCollapsed ? '»' : '«'}
            </button>
          </div>
          {!toolboxCollapsed && (
            <div className={styles.toolboxContent}>
              <FieldToolbox hideHeader />
              <div className={styles.toolboxSection}>
                <h3>Info</h3>
                <label style={{ fontSize: 12, color: '#adb5bd', marginBottom: 4, display: 'block' }}>Title</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  onBlur={() => {
                    onTemplateNameSave(templateName, { revertInvalid: true }).catch(() => {});
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  }}
                  className={styles.templateNameInput}
                />
                <p style={{ fontSize: 12, color: '#adb5bd', margin: '4px 0 0' }}>
                  {`${fieldCount} ${fieldCountLabel}`}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className={styles.pdfArea}>
          <div className={styles.pdfToolbar}>
            <button className={styles.backBtn} onClick={onBack}>
              ← Back
            </button>
            <button className={styles.deleteTemplateBtn} onClick={() => setDeleteModalOpen(true)}>
              Delete Template
            </button>
            <button disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
              ‹ Prev
            </button>
            <span>
              {`Page ${currentPage} / ${numPages}`}
            </span>
            <button disabled={currentPage >= numPages} onClick={() => setCurrentPage((p) => p + 1)}>
              Next ›
            </button>
            <div className={styles.zoomControls}>
              <button onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}>−</button>
              <span>{`${Math.round(scale * 100)}%`}</span>
              <button onClick={() => setScale((s) => Math.min(2, s + 0.1))}>+</button>
            </div>
            <button
              type="button"
              className={`${styles.snapToggleBtn} ${snappingEnabled ? styles.snapToggleBtnActive : ''}`}
              onClick={() => setSnappingEnabled((enabled) => !enabled)}
            >
              {`Snap: ${snappingEnabled ? 'On' : 'Off'}`}
            </button>
            <button className={styles.testBtn} onClick={() => setTestModalOpen(true)}>
              Test Form
            </button>
            <button className={`${styles.saveBtn} ${showSaved ? styles.saveBtnSaved : ''}`} onClick={onSave}>
              {showSaved ? 'Saved!' : 'Save Fields'}
            </button>
          </div>

          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className={styles.pdfCanvasWrapper}
            ref={pdfCanvasWrapperRef}
            onClick={onDeselect}
            onPointerDown={onMarqueePointerDown}
            onPointerMove={onMarqueePointerMove}
            onPointerUp={onMarqueePointerUp}
          >
            <div className={styles.pdfPageContainer} ref={pdfContainerRef}>
              <PdfViewer
                pdfUrl={template.pdf_url}
                scale={scale}
                currentPage={currentPage}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
                onPageLoadSuccess={onPageLoadSuccess}
              >
                <FieldOverlay
                  fields={editorFields}
                  currentPage={currentPage}
                  selectedFieldIds={selectedFieldIds}
                  scale={scale}
                  onSelect={onFieldSelect}
                  onToggleSelect={onFieldToggleSelect}
                  onResize={onFieldResize}
                  onResizeBegin={onResizeBegin}
                  onResizeEnd={onResizeEnd}
                  snapDimensions={snapDimensions}
                  activeDrag={activeDrag}
                  marquee={marquee}
                  snapGuides={snapGuides}
                />
              </PdfViewer>
            </div>
          </div>
        </div>

        <div className={`${styles.propertiesWrapper} ${propertiesCollapsed ? styles.propertiesWrapperCollapsed : ''}`}>
          <div className={styles.panelHeader}>
            <button
              type="button"
              className={styles.panelToggleBtn}
              onClick={() => setPropertiesCollapsed((c) => !c)}
            >
              {propertiesCollapsed ? '«' : '»'}
            </button>
            {!propertiesCollapsed && <h3>Field Properties</h3>}
          </div>
          {!propertiesCollapsed && (
            <FieldProperties
              fields={selectedFields}
              tokenGroups={tokenGroups}
              onUpdate={onFieldUpdate}
              onBulkUpdate={onBulkUpdate}
              onDelete={onFieldDelete}
              onBulkDelete={onBulkDelete}
              hideHeader
            />
          )}
        </div>
      </div>
      <TestFormModal
        isOpen={testModalOpen}
        templateId={template.id}
        companyId={template.company_id}
        onClose={() => setTestModalOpen(false)}
      />
      <DeleteTemplateModal
        isOpen={deleteModalOpen}
        template={template}
        onClose={onDeleteClose}
      />
    </DndContext>
  );
}

const PdfFormEditorMemo = memo(PdfFormEditor, areEqual);
export { PdfFormEditorMemo as PdfFormEditor };
